import i18n from '@/utils/i18n';
import React from 'react';
import { ItemsContext_VariantsPackFieldsFragment } from '@/types';
import { QuantitySelector } from '@/components/quantity-selector';
import { ScannerVariantPackListItem } from '@/components/scanner-variant-pack-list-item';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useGetAddToOrderDisabled } from '@/hooks/use-add-to-order-disabled';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useScannerContext } from '@/contexts/scanner-context';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  variantPack: ItemsContext_VariantsPackFieldsFragment;
}

export const ScanResultListItem: React.FC<Props> = React.memo(
  ({ variantPack }) => {
    const theme = useTheme();
    const adaptVariantPackToItemListOption =
      useAdaptVariantPackToItemListOption();
    const getIsItemDisabled = useGetAddToOrderDisabled();
    const { debouncedUpdateQuantity } = useNewOrderActionsContext();
    const { setMultiVariantPackUpdates } = useScannerContext();

    const variantPackUUID = variantPack.uuid;
    const option = adaptVariantPackToItemListOption(variantPack);
    const isUnavailable = getIsItemDisabled(variantPackUUID);
    const quantity = useItemVisibleQuantity(variantPackUUID);

    return (
      <View style={styles.scanListItemWrapper}>
        <ScannerVariantPackListItem
          option={option}
          style={styles.flex}
          unitPriceInMicros={option.metadata?.unitPriceInMicros}
        />
        {isUnavailable ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-md-600"
          >
            {i18n.t('unavailable')}
          </Typography>
        ) : (
          <QuantitySelector
            isFocused={false}
            maxQuantity={option.metadata?.maximumQuantity}
            minQuantity={option.metadata?.minimumQuantity}
            onChange={(previousQuantity, newQuantity) => {
              debouncedUpdateQuantity(
                {
                  eventPayloads: null,
                  newQuantity,
                  previousQuantity,
                  variantPackDetails: {
                    itemDisplayName: option.metadata!.displayName,
                    metadata: variantPack.metadata,
                  },
                  variantPackUUID,
                },
                { source: 'scanner' },
              );
              setMultiVariantPackUpdates((prev) => ({
                ...prev,
                [variantPackUUID]: {
                  quantity: newQuantity,
                  variantPack,
                },
              }));
            }}
            quantity={quantity}
          />
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  scanListItemWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
  },
});
